import { useRef } from 'react';
import { Badge, Button, Callout, Card, EmptyState, Stat, Table, Td } from '../ui/primitives';
import { relative } from '../../lib/format';

/**
 * Who actually sends mail as this domain, from the aggregate reports
 * receivers have sent back.
 *
 * The table leads with the verdict rather than with volume, because the row
 * that matters is rarely the biggest one. A service that authenticates for
 * its own domain but does not align is invisible in DNS, looks healthy in
 * any inbox, and is precisely what starts bouncing the day the policy moves
 * to reject - so those sort to the top and are named.
 */

export type SourceVerdict = 'aligned' | 'unaligned' | 'unauthenticated' | 'mixed';

export interface DmarcSource {
  sourceIp: string;
  messages: number;
  passed: number;
  failed: number;
  dkimAligned: number;
  spfAligned: number;
  dkimAuthenticated: number;
  spfAuthenticated: number;
  delivered: number;
  quarantined: number;
  rejected: number;
  dkimDomains: string[];
  spfDomains: string[];
  headerFroms: string[];
  reporters: string[];
  overrideReasons: string[];
  firstSeen: string;
  lastSeen: string;
  verdict: SourceVerdict;
  passRate: number;
}

export interface DmarcReadiness {
  safeToEnforce: boolean;
  totalMessages: number;
  alignedMessages: number;
  unalignedMessages: number;
  unauthenticatedMessages: number;
  alignmentRate: number;
  blockers: DmarcSource[];
  reporters: number;
  daysCovered: number;
  summary: string;
}

export interface DmarcSourcesData {
  domain: string;
  policyDomain: string;
  sources: DmarcSource[];
  readiness: DmarcReadiness;
  coverage: { daysCovered: number; reporters: number; reports: number; latest: string | null };
  mailboxConfigured: boolean;
}

const VERDICT: Record<
  SourceVerdict,
  { label: string; tone: 'good' | 'warn' | 'bad' | 'info'; meaning: string }
> = {
  aligned: {
    label: 'aligned',
    tone: 'good',
    meaning: 'Authenticates and aligns. This is your own mail, working.',
  },
  unaligned: {
    label: 'not aligned',
    tone: 'bad',
    meaning:
      'Authenticates for another domain but does not align with yours. A real service sending as you - this is what an enforced policy will start blocking.',
  },
  mixed: {
    label: 'partly aligned',
    tone: 'warn',
    meaning:
      'Some of its mail aligns and some does not. Usually forwarding, which breaks SPF; check whether DKIM survives.',
  },
  unauthenticated: {
    label: 'unauthenticated',
    tone: 'info',
    meaning:
      'Nothing authenticates. Either spoofing, or a sender with no setup at all - this is the traffic a policy is meant to stop.',
  },
};

export function DmarcSourcesPanel({
  data,
  days,
  onDaysChange,
  onIngest,
  ingesting,
  onUpload,
  uploading,
}: {
  data: DmarcSourcesData | null;
  days: number;
  onDaysChange: (days: number) => void;
  onIngest?: () => void;
  ingesting?: boolean;
  onUpload?: (files: FileList) => void;
  uploading?: boolean;
}) {
  const fileInput = useRef<HTMLInputElement | null>(null);

  /**
   * Upload is offered whether or not a mailbox is configured. It is the way
   * in before IMAP exists, and it stays useful after: reports already sitting
   * in an inbox, or downloaded from a third-party processor, load today
   * rather than in two days' time.
   */
  const uploadControl = onUpload ? (
    <>
      <input
        ref={fileInput}
        type="file"
        multiple
        accept=".zip,.gz,.xml,application/zip,application/gzip,text/xml,application/xml"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onUpload(e.target.files);
          // Reset so re-picking the same file fires onChange again.
          e.target.value = '';
        }}
      />
      <Button variant="secondary" onClick={() => fileInput.current?.click()} disabled={uploading}>
        {uploading ? 'Reading...' : 'Upload reports'}
      </Button>
    </>
  ) : null;

  const action = (
    <div className="flex gap-2">
      {uploadControl}
      {onIngest && data?.mailboxConfigured && (
        <Button variant="secondary" onClick={onIngest} disabled={ingesting}>
          {ingesting ? 'Collecting...' : 'Collect reports now'}
        </Button>
      )}
    </div>
  );

  if (!data || (!data.mailboxConfigured && !data.sources.length)) {
    return (
      <Card
        title="Who sends as this domain"
        subtitle="From the aggregate reports receivers email back."
        actions={action}
      >
        <EmptyState
          title="No reports yet"
          body="Receivers email these reports to whatever is in each domain's rua= tag; there is no API to query. Upload report files you already have to see this straight away, or point rua= at a mailbox and add it under Settings - Connected services - Dmarc to have them collected every six hours."
        />
      </Card>
    );
  }

  if (!data.sources.length) {
    return (
      <Card
        title="Who sends as this domain"
        subtitle={'Reports filed under ' + data.policyDomain + '.'}
        actions={action}
      >
        <EmptyState
          title="No reports yet"
          body="Receivers batch a day at a time and send the first report 24-48 hours after traffic starts, so nothing here does not mean nothing is wrong yet."
        />
      </Card>
    );
  }

  const r = data.readiness;

  return (
    <Card
      title="Who sends as this domain"
      subtitle={
        'Reports filed under ' +
        data.policyDomain +
        (data.policyDomain !== data.domain.toLowerCase()
          ? ' (this subdomain has no record of its own, so its reports go to the parent)'
          : '') +
        '. ' +
        data.coverage.reports +
        ' report(s) from ' +
        data.coverage.reporters +
        ' receiver(s) over ' +
        data.coverage.daysCovered +
        ' day(s).'
      }
      actions={action}
    >
      <div className="mb-5">
        <Callout
          tone={r.safeToEnforce ? 'good' : r.unalignedMessages ? 'warn' : 'info'}
          title={
            r.safeToEnforce
              ? 'Safe to raise the policy'
              : r.unalignedMessages
                ? 'Not safe to raise the policy yet'
                : 'Still gathering data'
          }
        >
          {r.summary}
        </Callout>
      </div>

      <div className="grid gap-4 sm:grid-cols-4">
        <Stat label="Messages seen" value={r.totalMessages.toLocaleString()} hint={'over ' + r.daysCovered + ' days'} />
        <Stat
          label="Aligned"
          value={r.alignmentRate + '%'}
          tone={r.alignmentRate >= 99 ? 'good' : r.alignmentRate >= 90 ? 'warn' : 'bad'}
          hint={r.alignedMessages.toLocaleString() + ' message(s)'}
        />
        <Stat
          label="Would break"
          value={r.unalignedMessages.toLocaleString()}
          tone={r.unalignedMessages ? 'bad' : 'good'}
          hint="legitimate mail that does not align"
        />
        <Stat
          label="Unauthenticated"
          value={r.unauthenticatedMessages.toLocaleString()}
          hint="what the policy is meant to stop"
        />
      </div>

      <div className="mt-5 flex items-center gap-2">
        <span className="text-xs text-ink-500">Window</span>
        {[7, 30, 90].map((d) => (
          <Button key={d} variant={d === days ? 'primary' : 'ghost'} onClick={() => onDaysChange(d)}>
            {d} days
          </Button>
        ))}
      </div>

      <div className="mt-4">
        <Table head={['Source IP', 'Verdict', 'Messages', 'Aligned', 'Authenticates for', 'Last seen']}>
          {data.sources.map((source) => (
            <tr key={source.sourceIp}>
              <Td className="whitespace-nowrap font-mono text-xs font-medium text-ink-900">
                {source.sourceIp}
                {source.reporters.length > 0 && (
                  <div className="mt-0.5 font-sans text-[10px] text-ink-400">
                    reported by {source.reporters.join(', ')}
                  </div>
                )}
              </Td>
              <Td>
                <Badge tone={VERDICT[source.verdict].tone}>{VERDICT[source.verdict].label}</Badge>
                {source.overrideReasons.length > 0 && (
                  <div className="mt-0.5 text-[10px] text-ink-400">
                    {source.overrideReasons.join(', ')}
                  </div>
                )}
              </Td>
              <Td className="tabular-nums">
                {source.messages.toLocaleString()}
                <div className="mt-0.5 text-[10px] text-ink-400">
                  {source.quarantined > 0 && source.quarantined + ' quarantined '}
                  {source.rejected > 0 && source.rejected + ' rejected'}
                </div>
              </Td>
              <Td className="tabular-nums">
                {source.passRate}%
                <div className="mt-0.5 text-[10px] text-ink-400">
                  {source.dkimAligned > 0 && 'dkim ' + source.dkimAligned + ' '}
                  {source.spfAligned > 0 && 'spf ' + source.spfAligned}
                  {source.dkimAligned === 0 && source.spfAligned === 0 && 'neither'}
                </div>
              </Td>
              <Td className="max-w-[16rem] break-all font-mono text-[11px] text-ink-600">
                {[...new Set([...source.dkimDomains, ...source.spfDomains])].join(', ') || (
                  <span className="font-sans text-ink-400">nothing passed</span>
                )}
              </Td>
              <Td className="whitespace-nowrap text-xs">{relative(source.lastSeen)}</Td>
            </tr>
          ))}
        </Table>
      </div>

      {r.blockers.length > 0 && (
        <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-900">
            Fix these before raising the policy
          </p>
          <ul className="mt-2 space-y-1.5">
            {r.blockers.map((b) => (
              <li key={b.sourceIp} className="text-xs leading-relaxed text-amber-900">
                <span className="font-mono font-medium">{b.sourceIp}</span>
                {' - '}
                {b.failed.toLocaleString()} message(s) that do not align
                {[...new Set([...b.dkimDomains, ...b.spfDomains])].length > 0 && (
                  <>
                    , authenticating as{' '}
                    <span className="font-mono">
                      {[...new Set([...b.dkimDomains, ...b.spfDomains])].join(', ')}
                    </span>
                  </>
                )}
                . {VERDICT[b.verdict].meaning}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
